import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'types/store';
import { Vehicle, VehicleAddAmendment } from 'types/expiryRenewal';
import { setAmendments } from 'actions/expiryRenewalActions';
import useVehicleForm from './useVehicleForm';
import { buildTakenRegs, buildVehicleAmendFields } from '../utils/amendmentUtils';

export type { FieldErrors } from './useVehicleForm';

const emptyFields: Vehicle = { Registration: '', Make: '', Model: '', YearOfManufacture: '' };

const useAddVehicle = (onClose: () => void) => {
  const dispatch = useDispatch();
  const pageModel = useSelector((state: Store) => state.expiryRenewal.pageModel);
  const existingAmendments = useSelector((state: Store) => state.expiryRenewal.amendments);

  const takenRegs = useMemo(
    () => buildTakenRegs(pageModel?.vehicles ?? [], existingAmendments),
    [pageModel?.vehicles, existingAmendments],
  );

  const computeIsDuplicate = useCallback((reg: string) => takenRegs.has(reg), [takenRegs]);

  const {
    fields,
    searchedFuelType,
    touched,
    loading,
    errors,
    searchAndRegError,
    isVehicleNotFound,
    clearRegistrationSearchError,
    setTouched,
    handleChange,
    handleBlur,
    handleSearch,
  } = useVehicleForm(emptyFields, computeIsDuplicate);

  const hasErrors = Object.values(errors).some(Boolean);
  const allFilled = fields.Registration && fields.Make && fields.Model && fields.YearOfManufacture;
  const isSaveDisabled = loading || !allFilled || hasErrors;

  const handleSave = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ Registration: true, Make: true, Model: true, YearOfManufacture: true });
    if (hasErrors || !pageModel) return;

    const newAmendment: VehicleAddAmendment = {
      productCode: 'ExtraVehicle',
      amendmentType: 'Add',
      vehicleAdd: { ...buildVehicleAmendFields(fields, searchedFuelType), registration: fields.Registration },
    };
    dispatch(setAmendments([...existingAmendments, newAmendment]));
    onClose();
  }, [fields, hasErrors, pageModel, existingAmendments, searchedFuelType, dispatch, onClose, setTouched]);

  return {
    fields,
    errors,
    touched,
    loading,
    isSaveDisabled,
    searchAndRegError,
    isVehicleNotFound,
    clearRegistrationSearchError,
    handleChange,
    handleBlur,
    handleSearch,
    handleSave,
  };
};

export default useAddVehicle;
